import isNil from 'lodash/isNil';
import { SetShapePropertiesMutation } from '../mutation/shape/set-shape-properties-mutation';
import { ComputeGroupTransformMutation } from '../mutation/shape/compute-group-transform-mutation';
import { TransformShapeMutation } from '../mutation/shape/transform-shape-mutation';
import { DataType, DataTypeUtils } from '../data';
import { shallowOmit } from '../utils/object-utils';
import { record } from '../utils/recorder';
import { cloneDeep } from '../utils/clone-deep';
import { merge } from '../utils/protoutil';
import { MutationAPI } from './mutation-api';
import type { DeltaOf, ShapeOf } from '../utils/delta';
import type { BaseShapeDataModel } from '../model/base-shape-data-model';
import type { IDataModel } from '../model/data-model';
import type {
    ICTGroupShapePropertiesDelta,
    ICTGroupTransform2D,
    ICTShapePropertiesDelta,
    ICTTransform2D,
    ILockingDelta,
    INonVisualPropertiesDelta,
    IShapeDelta,
} from '@tencent/slide-sdk-pb-model';

export abstract class BaseShapeMutationAPI<Model extends BaseShapeDataModel<any>> extends MutationAPI<Model> {
    /**
     * 更新形状的位置、大小、旋转等属性
     * @param transform 形状的位置、大小、旋转等变更数据
     */
    @record()
    public updateTransform(transform: NonNullable<DeltaOf<ICTTransform2D & ICTGroupTransform2D>>) {
        const chXfrmDelta =
            this.model.type === DataType.GroupShape
                ? {
                      chOff: transform.chOff,
                      chExt: transform.chExt,
                  }
                : undefined;

        this.transaction(() => {
            this.request([
                new TransformShapeMutation({
                    shapeId: this.model.id,
                    shapeType: DataTypeUtils.toShapeType(this.model.type),
                    pageId: this.model.getPage().id,
                    parentId: this.model.parent.id,
                    xfrmDelta: shallowOmit(transform, 'chOff', 'chExt'),
                    chXfrmDelta,
                }),
            ]);
            this.computeParentTransform(this.model.parent);
        });
    }

    /**
     * 更新形状属性
     * @param shapeDelta 形状属性变更数据
     */
    @record()
    protected updateProps(
        shapeDelta: Omit<IShapeDelta, 'spPr'> & {
            spPr?: ICTShapePropertiesDelta | ICTGroupShapePropertiesDelta | null;
        },
    ) {
        this.transaction(() => {
            const xfrm = shapeDelta.spPr?.xfrm;
            if (xfrm) {
                this.updateTransform(xfrm);
            }

            if (this.isTransformOnly(shapeDelta)) {
                return;
            }

            this.request([
                new SetShapePropertiesMutation({
                    shapeId: this.model.id,
                    pageId: this.model.getPage().id,
                    shapeDelta: {
                        ...shapeDelta,
                        spPr: shapeDelta.spPr && shallowOmit(shapeDelta.spPr, 'xfrm'),
                    },
                }),
            ]);
        });
    }

    protected getCommonNonVisualPropsDelta(
        nvPrDelta: DeltaOf<Omit<INonVisualPropertiesDelta, 'shape' | 'connector' | 'picture'>>,
        overrideProps?: Exclude<ShapeOf<INonVisualPropertiesDelta>, true>,
    ): INonVisualPropertiesDelta {
        const finalOverrideProps = {
            addon: true,
            attachment: true,
            ...overrideProps,
        };
        const nvPr = this.getDeltaByOverride(this.model.store.data.nvPr, nvPrDelta, finalOverrideProps);
        return (
            nvPr && {
                ...shallowOmit(nvPr, 'txBox', 'preferRelativeResize', 'stCxn', 'endCxn'),
                userDrawn: nvPr.userDrawn === null ? false : nvPr.userDrawn,
                isPhoto: nvPr.isPhoto === null ? false : nvPr.isPhoto,
                locking: this.getLockingDelta(nvPr.locking),
                attachment: nvPr.attachment,
                addon: nvPr.addon,
            }
        );
    }

    protected getLockingDelta(locking?: DeltaOf<ILockingDelta> | null): ILockingDelta | undefined | null {
        if (isNil(locking)) return locking;

        const delta: ILockingDelta = {};
        for (const [key, value] of Object.entries(locking)) {
            delta[key as keyof ILockingDelta] = value === null ? false : value;
        }

        return delta;
    }

    protected computeParentTransform(parent: IDataModel) {
        if (parent.type === DataType.GroupShape && parent.parent.type === DataType.GroupShape) {
            // TODO: 可以判定一下是否需要改变父元素的大小再添加 mutation
            this.request([
                new ComputeGroupTransformMutation({
                    pageId: this.model.getPage().id,
                    groupId: parent.id,
                    parentId: parent.parent.id,
                }),
            ]);
            this.computeParentTransform(parent.parent);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getDeltaByOverride(data: any, delta: any, overrideProps: any) {
        if (overrideProps === true) {
            return merge(cloneDeep(data), delta);
        }
        for (const key of Object.keys(overrideProps)) {
            if (!data[key] && !delta[key]) {
                continue;
            }
            delta[key] = this.getDeltaByOverride(data[key] ?? {}, delta[key] ?? {}, overrideProps[key]);
        }
        return delta;
    }

    private isTransformOnly(shapeDelta: IShapeDelta) {
        return (
            !!shapeDelta.spPr?.xfrm && Object.keys(shapeDelta).length === 1 && Object.keys(shapeDelta.spPr).length === 1
        );
    }
}
